import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import { api, API } from "@/lib/api";
import { CheckCircle, DownloadSimple, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { UnitToggle, toCm, toKg } from "@/components/UnitToggle";

// Step keys — labels are resolved via i18n at render time.
// NOTE (Phase 1): The wizard step labels and top-level toasts are i18n'd.
// Individual field labels (Nombre, Peso, Altura...) still render in Spanish
// and will be migrated to i18n keys in Phase 2 (see /app/memory/PRD.md).
const STEP_KEYS = ["data", "physical", "goal", "health", "habits", "confirm"];

export default function EvaluationForm() {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState({
    full_name: "",
    email: "",
    phone: "",
    age: 25,
    gender: "male",
    height: 170,
    height_unit: "cm",  // "cm" | "in"
    weight: 70,
    weight_unit: "kg",  // "kg" | "lb"
    body_fat_pct: "",
    measurements: { waist: "", hip: "", chest: "", arm: "", thigh: "" },
    measurements_unit: "cm",  // "cm" | "in" — shared for all body measurements
    objective: "fat_loss",
    training_level: "beginner",
    training_days_per_week: 3,
    activity_level: "moderate",
    health_history: "",
    eating_habits: "",
    notes: "",
  });

  const update = (k, v) => setData({ ...data, [k]: v });
  const updateM = (k, v) => setData({ ...data, measurements: { ...data.measurements, [k]: v } });

  const next = () => {
    if (step === 0 && (!data.full_name || !data.email)) {
      toast.error(t("evaluation.toast_missing_name_email"));
      return;
    }
    setStep(Math.min(step + 1, STEP_KEYS.length - 1));
  };
  const back = () => setStep(Math.max(step - 1, 0));

  const submit = async () => {
    setLoading(true);
    try {
      // Convert measurements (length) to cm if user picked inches
      const mUnit = data.measurements_unit;
      const measurementsCm = Object.fromEntries(
        Object.entries(data.measurements)
          .filter(([, v]) => v)
          .map(([k, v]) => [k, toCm(v, mUnit)])
      );
      const payload = {
        full_name: data.full_name,
        email: data.email,
        phone: data.phone,
        age: Number(data.age),
        gender: data.gender,
        height_cm: toCm(data.height, data.height_unit),
        weight_kg: toKg(data.weight, data.weight_unit),
        body_fat_pct: data.body_fat_pct ? Number(data.body_fat_pct) : null,
        measurements: measurementsCm,
        objective: data.objective,
        training_level: data.training_level,
        training_days_per_week: Number(data.training_days_per_week),
        activity_level: data.activity_level,
        health_history: data.health_history,
        eating_habits: data.eating_habits,
        notes: data.notes,
      };
      const { data: res } = await api.post("/evaluations", payload);
      setSubmitted(res);
      toast.success(t("evaluation.toast_submit_success"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("evaluation.toast_submit_error"));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <section className="min-h-[70vh] flex items-center justify-center px-4 py-24">
          <div className="max-w-xl text-center">
            <CheckCircle size={80} weight="duotone" color="#39FF14" className="mx-auto mb-6" />
            <h1 className="as-heading text-4xl text-white mb-3" data-testid="eval-success-title">
              ¡EVALUACIÓN <span className="text-as-green">RECIBIDA!</span>
            </h1>
            <p className="text-as-gray mb-8">
              Tu evaluación está en manos del coach. Descarga tu copia en PDF para tus registros.
            </p>
            <a
              href={`${API}/evaluations/${submitted.id}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="as-btn-primary inline-flex"
              data-testid="download-eval-pdf-btn"
            >
              Descargar PDF <DownloadSimple size={18} weight="bold" />
            </a>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="bg-as-black border-b border-as-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="as-label text-as-green mb-3">EVALUACIÓN PROFESIONAL</p>
          <h1 className="as-heading text-4xl sm:text-5xl text-white">
            CUÉNTANOS DE <span className="text-as-green">TI.</span>
          </h1>

          {/* progress bar */}
          <div className="mt-8">
            <div className="flex justify-between mb-2">
              {STEP_KEYS.map((k, i) => (
                <span
                  key={k}
                  className={`as-label text-[10px] ${i <= step ? "text-as-green" : "text-as-gray-dim"}`}
                >
                  {t(`evaluation.step_${k}`)}
                </span>
              ))}
            </div>
            <div className="h-1 bg-as-border">
              <div
                className="h-1 bg-as-green transition-all"
                style={{ width: `${((step + 1) / STEP_KEYS.length) * 100}%` }}
                data-testid="eval-progress"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-as-surface py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="as-card !p-8">
            {step === 0 && (
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Nombre completo *">
                  <input className="as-input" value={data.full_name} onChange={(e) => update("full_name", e.target.value)} data-testid="eval-name" />
                </Field>
                <Field label="Email *">
                  <input className="as-input" type="email" value={data.email} onChange={(e) => update("email", e.target.value)} data-testid="eval-email" />
                </Field>
                <Field label="Teléfono / WhatsApp">
                  <input className="as-input" value={data.phone} onChange={(e) => update("phone", e.target.value)} data-testid="eval-phone" />
                </Field>
                <Field label="Edad">
                  <input className="as-input" type="number" value={data.age} onChange={(e) => update("age", e.target.value)} data-testid="eval-age" />
                </Field>
                <Field label="Género">
                  <select className="as-input" value={data.gender} onChange={(e) => update("gender", e.target.value)} data-testid="eval-gender">
                    <option value="male">Masculino</option>
                    <option value="female">Femenino</option>
                  </select>
                </Field>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Altura">
                    <div className="flex gap-2">
                      <input
                        className="as-input flex-1"
                        type="number"
                        value={data.height}
                        onChange={(e) => update("height", e.target.value)}
                        data-testid="eval-height"
                      />
                      <UnitToggle
                        value={data.height_unit}
                        onChange={(u) => update("height_unit", u)}
                        options={[{ v: "cm", l: "CM" }, { v: "in", l: "IN" }]}
                        testid="eval-height-unit"
                      />
                    </div>
                  </Field>
                  <Field label="Peso">
                    <div className="flex gap-2">
                      <input
                        className="as-input flex-1"
                        type="number"
                        value={data.weight}
                        onChange={(e) => update("weight", e.target.value)}
                        data-testid="eval-weight"
                      />
                      <UnitToggle
                        value={data.weight_unit}
                        onChange={(u) => update("weight_unit", u)}
                        options={[{ v: "kg", l: "KG" }, { v: "lb", l: "LB" }]}
                        testid="eval-weight-unit"
                      />
                    </div>
                  </Field>
                  <Field label="% Grasa corporal (opcional)">
                    <input className="as-input" type="number" value={data.body_fat_pct} onChange={(e) => update("body_fat_pct", e.target.value)} />
                  </Field>
                </div>

                <div className="border-t border-as-border pt-5">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <p className="as-label text-as-green">MEDIDAS CORPORALES</p>
                    <UnitToggle
                      value={data.measurements_unit}
                      onChange={(u) => update("measurements_unit", u)}
                      options={[{ v: "cm", l: "CM" }, { v: "in", l: "IN" }]}
                      testid="eval-measurements-unit"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label={`Cintura (${data.measurements_unit})`}>
                      <input className="as-input" type="number" value={data.measurements.waist} onChange={(e) => updateM("waist", e.target.value)} data-testid="eval-waist" />
                    </Field>
                    <Field label={`Cadera (${data.measurements_unit})`}>
                      <input className="as-input" type="number" value={data.measurements.hip} onChange={(e) => updateM("hip", e.target.value)} />
                    </Field>
                    <Field label={`Pecho (${data.measurements_unit})`}>
                      <input className="as-input" type="number" value={data.measurements.chest} onChange={(e) => updateM("chest", e.target.value)} />
                    </Field>
                    <Field label={`Brazo (${data.measurements_unit})`}>
                      <input className="as-input" type="number" value={data.measurements.arm} onChange={(e) => updateM("arm", e.target.value)} />
                    </Field>
                    <Field label={`Muslo (${data.measurements_unit})`}>
                      <input className="as-input" type="number" value={data.measurements.thigh} onChange={(e) => updateM("thigh", e.target.value)} />
                    </Field>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Objetivo principal">
                  <select className="as-input" value={data.objective} onChange={(e) => update("objective", e.target.value)} data-testid="eval-objective">
                    <option value="fat_loss">Pérdida de grasa</option>
                    <option value="muscle_gain">Ganancia muscular</option>
                    <option value="recomposition">Recomposición corporal</option>
                    <option value="maintenance">Mantenimiento</option>
                    <option value="performance">Rendimiento deportivo</option>
                  </select>
                </Field>
                <Field label="Nivel de entrenamiento">
                  <select className="as-input" value={data.training_level} onChange={(e) => update("training_level", e.target.value)} data-testid="eval-level">
                    <option value="beginner">Principiante</option>
                    <option value="intermediate">Intermedio</option>
                    <option value="advanced">Avanzado</option>
                  </select>
                </Field>
                <Field label="Días disponibles por semana">
                  <input className="as-input" type="number" min="2" max="7" value={data.training_days_per_week} onChange={(e) => update("training_days_per_week", e.target.value)} />
                </Field>
                <Field label="Nivel de actividad diaria">
                  <select className="as-input" value={data.activity_level} onChange={(e) => update("activity_level", e.target.value)}>
                    <option value="sedentary">Sedentario</option>
                    <option value="light">Ligero</option>
                    <option value="moderate">Moderado</option>
                    <option value="active">Activo</option>
                    <option value="very_active">Muy activo</option>
                  </select>
                </Field>
              </div>
            )}

            {step === 3 && (
              <Field label="Historial de salud (lesiones, condiciones, medicamentos)">
                <textarea className="as-input min-h-[140px]" value={data.health_history} onChange={(e) => update("health_history", e.target.value)} data-testid="eval-health" />
              </Field>
            )}

            {step === 4 && (
              <Field label="Hábitos alimenticios (describe tus comidas típicas, restricciones, alergias)">
                <textarea className="as-input min-h-[140px]" value={data.eating_habits} onChange={(e) => update("eating_habits", e.target.value)} data-testid="eval-habits" />
              </Field>
            )}

            {step === 5 && (
              <div>
                <h3 className="as-heading text-xl text-white mb-4">RESUMEN</h3>
                <ul className="space-y-2 text-sm text-as-gray mb-6">
                  <li><b className="text-white">Nombre:</b> {data.full_name}</li>
                  <li><b className="text-white">Email:</b> {data.email}</li>
                  <li><b className="text-white">Edad:</b> {data.age} · {data.gender}</li>
                  <li><b className="text-white">Físico:</b> {data.height} {data.height_unit} / {data.weight} {data.weight_unit}</li>
                  <li><b className="text-white">Objetivo:</b> {data.objective}</li>
                  <li><b className="text-white">Nivel:</b> {data.training_level} · {data.training_days_per_week} días/sem</li>
                </ul>
                <Field label="Notas adicionales">
                  <textarea className="as-input min-h-[100px]" value={data.notes} onChange={(e) => update("notes", e.target.value)} />
                </Field>
              </div>
            )}

            <div className="flex justify-between mt-10">
              <button onClick={back} disabled={step === 0} className="as-btn-secondary disabled:opacity-30" data-testid="eval-back">
                <ArrowLeft size={16} weight="bold" /> {t("common.back")}
              </button>
              {step < STEP_KEYS.length - 1 ? (
                <button onClick={next} className="as-btn-primary" data-testid="eval-next">
                  {t("common.next")} <ArrowRight size={16} weight="bold" />
                </button>
              ) : (
                <button onClick={submit} disabled={loading} className="as-btn-primary" data-testid="eval-submit">
                  {loading ? t("evaluation.submit_loading") : t("evaluation.submit")}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="as-label block mb-2">{label}</span>
    {children}
  </label>
);
